import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, ChevronDown, Check, Square, Trash2, Calendar, NotebookPen, Loader2, Crown, ChevronRight, FileDown, Home, Sparkles, Play, Paperclip, BookOpen } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { jsPDF } from 'jspdf';
import { useAuth } from '../context/AuthContext';
import { journalCategories, journalItemLabels } from '../data/journalChecklist';
import { exerciseVideos } from '../data/exerciseVideos';
import { compressImage } from '../utils/imageCompress';
import { VipSwitcher } from './CoachingPage';
import ChecklistSearch from '../components/ChecklistSearch';
import MyQuizResults from '../components/MyQuizResults';
import useCoachingResources, { buildCoachingCategory } from '../hooks/useCoachingResources';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 9 Pillars for the in-session wellness wheel (ids match journalChecklist nine-pillars item ids)
const WHEEL_PILLARS = [
  { id: 'pil-breathing', name: '1 · Breathing & Being', short: 'Breathing' },
  { id: 'pil-nourishment', name: '2 · Nourishment & Timing', short: 'Nourishment' },
  { id: 'pil-thoughts', name: '3 · Thoughts & Inner Dialogue', short: 'Thoughts' },
  { id: 'pil-hydration', name: '4 · Hydration & Flow', short: 'Hydration' },
  { id: 'pil-movement', name: '5 · Movement & Body Awareness', short: 'Movement' },
  { id: 'pil-sleep', name: '6 · Sleep, Rest & Nervous System', short: 'Sleep' },
  { id: 'pil-emotional', name: '7 · Emotional Awareness', short: 'Emotional' },
  { id: 'pil-integration', name: '8 · Whole Self Integration', short: 'Whole Self' },
  { id: 'pil-context', name: '9 · The Self in Context', short: 'In Context' },
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const prettyDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
};

// Practitioner-only inline form to add a reusable custom exercise (name + optional how-to)
const AddCustomExercise = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const name = label.trim();
    if (!name) return;
    setSaving(true);
    await onAdd(name, desc.trim());
    setSaving(false);
    setLabel(''); setDesc(''); setOpen(false);
  };
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-sm font-body text-forest bg-forest/10 hover:bg-forest/20 rounded-full px-3 py-1.5 transition-colors"
        data-testid="add-exercise-open"
      >
        <Plus size={14} /> Add exercise
      </button>
    );
  }
  return (
    <div className="bg-white border border-forest/20 rounded-xl p-3 space-y-2" data-testid="add-exercise-form">
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Exercise name (e.g. Standing calf release)"
        className="w-full text-sm font-body text-stone-700 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-terracotta"
        data-testid="add-exercise-name"
        autoFocus
      />
      <textarea
        rows={2}
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="Short how-to (optional) — e.g. 2 mins each side, slow and gentle"
        className="w-full text-sm font-body text-stone-700 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-terracotta"
        data-testid="add-exercise-desc"
      />
      <div className="flex items-center gap-2">
        <button
          onClick={submit}
          disabled={saving || !label.trim()}
          className="inline-flex items-center gap-1.5 bg-forest text-cream text-sm font-body px-4 py-1.5 rounded-full hover:bg-forest-dark transition-colors disabled:opacity-50"
          data-testid="add-exercise-save"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
        </button>
        <button
          onClick={() => { setOpen(false); setLabel(''); setDesc(''); }}
          className="text-sm font-body text-stone-400 hover:text-stone-600 px-2 py-1.5 transition-colors"
          data-testid="add-exercise-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const WellnessJournalPage = ({
  board = 'journal',
  categories = journalCategories,
  activeTab = 'journal',
  eyebrow = 'Session Log',
  heading = 'Wellness Journal',
  intro = 'A dated log for each session — tick what we worked on, add notes, and plan the next visit. Everything saves to your account automatically.',
  ownerId = null,
  ownerEmail = '',
  ownerName = '',
  bypassGate = false,
  practitioner = false,
  headerSlot = null,
} = {}) => {
  const { user, hasAccess, loading, refreshUser } = useAuth();
  const { resources: coachingResources } = useCoachingResources();
  const owner = ownerId ? { id: ownerId, email: ownerEmail, name: ownerName } : user;
  const showHomework = board === 'jills-corner';
  // In Jill's Corner, clients see a clean "plan" of only the exercises Jill ticked.
  // Jill (practitioner) still ticks from the full menu and sees a live preview.
  // The "Journal" tab is a READ-ONLY MIRROR of the client's Jill's Corner sessions
  // (plan + 9-Pillars wheel) plus a personal notes box — no self-track menu.
  const mirror = board === 'journal';
  const fetchBoard = mirror ? 'jills-corner' : board;
  const isCorner = board === 'jills-corner';
  const clientView = (isCorner && !practitioner) || mirror; // read-only plan view
  const showMenu = !clientView;                              // full self-track menu (practitioner/personal only)
  const showPlan = isCorner || mirror;                       // the ticked-exercise plan box
  const planFirstName = (owner?.name || '').trim().split(' ')[0] || '';
  const [entries, setEntries] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [openCats, setOpenCats] = useState({});
  const [openDescs, setOpenDescs] = useState({});
  const [customExercises, setCustomExercises] = useState([]);
  const saveTimers = useRef({});

  // Admin creds (set by JillsCornerPage on practitioner sign-in) for custom-exercise CRUD
  const getAdminAuth = () => {
    try { return JSON.parse(sessionStorage.getItem('gw_admin_auth') || 'null'); } catch { return null; }
  };

  // Reusable "My Exercises" list Jill manages herself
  useEffect(() => {
    fetch(`${API}/custom-exercises`)
      .then((r) => r.json())
      .then((d) => setCustomExercises(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const addCustomExercise = async (label, desc) => {
    const auth = getAdminAuth();
    if (!auth) return;
    const res = await fetch(
      `${API}/admin/custom-exercises?admin_email=${encodeURIComponent(auth.email)}&admin_password=${encodeURIComponent(auth.password)}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ label, desc }) }
    );
    if (res.ok) {
      const data = await res.json();
      setCustomExercises((prev) => [...prev, data.exercise]);
    }
  };

  const deleteCustomExercise = async (id) => {
    const auth = getAdminAuth();
    if (!auth) return;
    const res = await fetch(
      `${API}/admin/custom-exercises/${id}?admin_email=${encodeURIComponent(auth.email)}&admin_password=${encodeURIComponent(auth.password)}`,
      { method: 'DELETE' }
    );
    if (res.ok) setCustomExercises((prev) => prev.filter((c) => c.id !== id));
  };

  // ── Session attachments (practitioner uploads a file into the client's plan) ──
  const [uploadingId, setUploadingId] = useState(null);

  // Shrink/convert big phone photos (incl. iPhone HEIC) before upload — shared util.

  const uploadAttachment = async (entryId, fileObj) => {
    const auth = getAdminAuth();
    if (!auth || !fileObj) return;
    setUploadingId(entryId);
    try {
      const toSend = await compressImage(fileObj);
      const fd = new FormData();
      fd.append('file', toSend);
      const res = await fetch(
        `${API}/journal/${entryId}/attachment?admin_email=${encodeURIComponent(auth.email)}&admin_password=${encodeURIComponent(auth.password)}`,
        { method: 'POST', body: fd }
      );
      if (res.ok) {
        const att = await res.json();
        setEntries((prev) => prev.map((e) => e.id === entryId ? { ...e, attachments: [...(e.attachments || []), att] } : e));
      } else {
        const msg = res.status === 413 ? 'That file is too large (max 100 MB).' : 'Upload failed — please try again.';
        alert(msg);
      }
    } catch {
      alert('Upload failed — please check your connection and try again.');
    } finally {
      setUploadingId(null);
    }
  };

  const deleteAttachment = async (entryId, attId) => {
    const auth = getAdminAuth();
    if (!auth) return;
    const res = await fetch(
      `${API}/journal/${entryId}/attachment/${attId}?admin_email=${encodeURIComponent(auth.email)}&admin_password=${encodeURIComponent(auth.password)}`,
      { method: 'DELETE' }
    );
    if (res.ok) setEntries((prev) => prev.map((e) => e.id === entryId ? { ...e, attachments: (e.attachments || []).filter((a) => a.id !== attId) } : e));
  };

  const renderAttachments = (entry) => {
    const atts = entry.attachments || [];
    if (!practitioner && atts.length === 0) return null;
    const fileUrl = (a) => `${API}/files/${a.storage_path}`;
    return (
      <div data-testid={`session-attachments-${entry.id}`}>
        <p className="font-heading text-sm text-forest mb-2 flex items-center gap-1.5">
          <Paperclip size={14} className="text-[#8a6a5a]" /> Recordings &amp; files
        </p>
        {atts.length === 0 ? (
          <p className="font-body text-xs text-stone-400 italic mb-2">No files added yet.</p>
        ) : (
          <div className="space-y-3 mb-2">
            {atts.map((a) => {
              const url = fileUrl(a);
              const ct = a.content_type || '';
              return (
                <div key={a.id} className="bg-stone-50 border border-stone-200 rounded-xl p-3" data-testid={`attachment-${entry.id}-${a.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className="font-body text-xs text-stone-600 truncate">{a.filename}</span>
                    {practitioner && (
                      <button onClick={() => deleteAttachment(entry.id, a.id)} className="text-stone-400 hover:text-red-600 transition-colors flex-shrink-0" title="Remove file" data-testid={`attachment-delete-${entry.id}-${a.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  {ct.startsWith('video/') ? (
                    <video controls playsInline preload="metadata" src={url} className="w-full rounded-lg max-h-80 bg-black" data-testid={`attachment-video-${entry.id}-${a.id}`} />
                  ) : ct.startsWith('audio/') ? (
                    <audio controls preload="metadata" src={url} className="w-full" data-testid={`attachment-audio-${entry.id}-${a.id}`} />
                  ) : ct.startsWith('image/') ? (
                    <img src={url} alt={a.filename} loading="lazy" className="w-full max-h-80 object-contain rounded-lg bg-stone-100" data-testid={`attachment-image-${entry.id}-${a.id}`} />
                  ) : (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-forest hover:text-terracotta underline text-sm font-body" data-testid={`attachment-download-${entry.id}-${a.id}`}>
                      <FileDown size={14} /> Open / download
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {practitioner && (
          <>
            <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-body text-forest bg-forest/5 border border-forest/20 rounded-full px-3 py-1.5 hover:bg-forest/10 transition-colors" data-testid={`attachment-upload-label-${entry.id}`}>
              {uploadingId === entry.id ? (<><Loader2 size={13} className="animate-spin" /> Uploading…</>) : (<><Plus size={13} /> Add a recording / file</>)}
              <input
                type="file"
                accept="video/*,audio/*,image/*,.pdf"
                className="hidden"
                disabled={uploadingId === entry.id}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadAttachment(entry.id, f); }}
                data-testid={`attachment-input-${entry.id}`}
              />
            </label>
            <p className="font-body text-[11px] text-stone-400 mt-1.5">Your client sees this in their plan for repeated playback. Video, audio, image or PDF — max 100 MB.</p>
          </>
        )}
      </div>
    );
  };


  // Append the Jill-managed "My Exercises" category so it ticks + shows in the plan everywhere.
  const withCoaching = (isCorner || mirror) && coachingResources.length
    ? [...categories, buildCoachingCategory(coachingResources)]
    : categories;
  const allCategories = (practitioner || customExercises.length > 0)
    ? [
        ...withCoaching,
        {
          id: 'my-exercises',
          name: 'My Exercises',
          blurb: 'Exercises you added yourself — reuse with any client.',
          layout: 'list',
          custom: true,
          items: customExercises.map((c) => ({ id: c.id, label: c.label, desc: c.desc })),
        },
      ]
    : withCoaching;

  useEffect(() => {
    if (user?.id) refreshUser(user.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEntries = useCallback(async () => {
    if (!owner?.id) { setFetching(false); return; }
    setFetching(true);
    try {
      const res = await fetch(`${API}/journal/${owner.id}?board=${fetchBoard}`);
      const data = await res.json();
      setEntries(data.entries || []);
      setOpenId((data.entries || []).length ? data.entries[0].id : null);
    } catch (e) {
      console.error('Failed to load journal', e);
    }
    setFetching(false);
  }, [owner?.id, fetchBoard]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const addEntry = async () => {
    try {
      const res = await fetch(`${API}/journal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: owner.id, user_email: owner.email, date: todayIso(), board }),
      });
      const entry = await res.json();
      setEntries((prev) => [entry, ...prev]);
      setOpenId(entry.id);
    } catch (e) { console.error(e); }
  };

  const persist = useCallback(async (id, patch) => {
    setSavingId(id);
    try {
      const res = await fetch(`${API}/journal/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const updated = await res.json();
      setEntries((prev) => prev.map((e) => (e.id === id ? updated : e)));
    } catch (e) { console.error(e); }
    setSavingId(null);
  }, []);

  // update local state immediately; debounce the save
  const updateEntry = (id, patch) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    clearTimeout(saveTimers.current[id]);
    saveTimers.current[id] = setTimeout(() => {
      const current = { ...(entries.find((e) => e.id === id) || {}), ...patch };
      persist(id, {
        date: current.date,
        checklist: current.checklist,
        item_notes: current.item_notes,
        pillar_scores: current.pillar_scores,
        session_notes: current.session_notes,
        next_plan: current.next_plan,
        homework: current.homework,
        homework_note: current.homework_note,
        homework_done: current.homework_done,
        client_notes: current.client_notes,
      });
    }, 700);
  };

  const toggleItem = (entry, itemId) => {
    const checklist = { ...(entry.checklist || {}), [itemId]: !entry.checklist?.[itemId] };
    updateEntry(entry.id, { checklist });
  };

  const toggleHomework = (entry, itemId) => {
    const homework = { ...(entry.homework || {}) };
    if (homework[itemId]) delete homework[itemId];
    else homework[itemId] = true;
    updateEntry(entry.id, { homework });
  };

  const toggleHomeworkDone = (entry, itemId) => {
    const homework_done = { ...(entry.homework_done || {}) };
    if (homework_done[itemId]) delete homework_done[itemId];
    else homework_done[itemId] = true;
    updateEntry(entry.id, { homework_done });
  };

  const setPillarScore = (entry, pillarId, val) => {
    const current = entry.pillar_scores?.[pillarId];
    const pillar_scores = { ...(entry.pillar_scores || {}) };
    if (current === val) delete pillar_scores[pillarId];
    else pillar_scores[pillarId] = val;
    updateEntry(entry.id, { pillar_scores });
  };

  const setItemNote = (entry, itemId, val) => {
    const item_notes = { ...(entry.item_notes || {}), [itemId]: val };
    updateEntry(entry.id, { item_notes });
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this session entry? This cannot be undone.')) return;
    try {
      await fetch(`${API}/journal/${id}`, { method: 'DELETE' });
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (e) { console.error(e); }
  };

  const countTicks = (entry) => Object.values(entry.checklist || {}).filter(Boolean).length;

  const downloadSessionPdf = (entry) => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 48;
    let y = margin;
    const ensureSpace = (needed) => { if (y + needed > pageH - margin) { doc.addPage(); y = margin; } };
    const line = (txt, x, size = 11, bold = false, color = [60, 60, 60]) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(size);
      doc.setTextColor(...color);
      const wrapped = doc.splitTextToSize(txt, pageW - margin - x);
      wrapped.forEach((w) => { ensureSpace(size + 5); doc.text(w, x, y); y += size + 5; });
    };

    // Header
    line('Grounded Wellness with Jill', margin, 20, true, [44, 62, 46]);
    line(clientView ? 'Your Session Plan' : 'Wellness Journal — Session Log', margin, 12, false, [140, 110, 90]);
    y += 4;
    doc.setDrawColor(220, 210, 180); doc.line(margin, y, pageW - margin, y); y += 20;

    line(`Client: ${entry.user_email || owner?.email || ''}`, margin, 11, true);
    line(`Session date: ${prettyDate(entry.date)}`, margin, 11, true);
    y += 8;

    // Ticked items by category (matches the on-screen plan order)
    allCategories.forEach((cat) => {
      if (cat.id === 'nine-pillars') return;
      const allItems = cat.groups ? cat.groups.flatMap((g) => g.items) : (cat.items || []);
      const ticked = allItems.filter((it) => entry.checklist?.[it.id]);
      if (!ticked.length) return;
      ensureSpace(30);
      line(cat.name, margin, 14, true, [44, 62, 46]);
      ticked.forEach((it) => {
        const hw = entry.homework?.[it.id] ? '   (homework)' : '';
        line(`•  ${journalItemLabels[it.id] || it.label}${hw}`, margin + 10, 11);
        const note = entry.item_notes?.[it.id];
        if (note) line(`     — ${note}`, margin + 20, 10, false, [120, 120, 120]);
      });
      y += 10;
    });

    // 9 Pillars wheel scores
    const scores = entry.pillar_scores || {};
    const rated = WHEEL_PILLARS.filter((p) => (scores[p.id] || 0) > 0);
    if (rated.length) {
      ensureSpace(30);
      line('The 9 Pillars — Wellness Wheel', margin, 14, true, [44, 62, 46]);
      const avg = (rated.reduce((a, p) => a + (scores[p.id] || 0), 0) / rated.length).toFixed(1);
      WHEEL_PILLARS.forEach((p) => {
        if (scores[p.id]) line(`•  ${p.name}:  ${scores[p.id]} / 10`, margin + 10, 11);
      });
      line(`Average: ${avg} / 10`, margin + 10, 11, true, [140, 110, 90]);
      y += 10;
    }

    // Notes
    if (entry.session_notes) {
      ensureSpace(30);
      line('What we did today', margin, 14, true, [44, 62, 46]);
      line(entry.session_notes, margin + 10, 11);
      y += 8;
    }
    if (entry.next_plan) {
      ensureSpace(30);
      line('Plan for next session', margin, 14, true, [44, 62, 46]);
      line(entry.next_plan, margin + 10, 11);
      y += 8;
    }
    if (entry.practitioner_notes) {
      ensureSpace(30);
      line('A note from Jill', margin, 14, true, [138, 106, 90]);
      line(entry.practitioner_notes, margin + 10, 11);
      y += 8;
    }
    if (entry.client_notes) {
      ensureSpace(30);
      line('My notes', margin, 14, true, [138, 106, 90]);
      line(entry.client_notes, margin + 10, 11);
      y += 8;
    }

    // Homework recap
    const hwItems = allCategories
      .flatMap((cat) => (cat.groups ? cat.groups.flatMap((g) => g.items) : (cat.items || [])))
      .filter((it) => entry.homework?.[it.id]);
    if (hwItems.length || entry.homework_note) {
      ensureSpace(30);
      line('Homework', margin, 14, true, [138, 106, 90]);
      hwItems.forEach((it) => line(`•  ${journalItemLabels[it.id] || it.label}`, margin + 10, 11));
      if (entry.homework_note) line(entry.homework_note, margin + 10, 11);
    }

    doc.save(`wellness-session-${entry.date || 'entry'}.pdf`);
  };

  // ── The session "plan": only the ticked exercises (what the client sees) ──
  const renderPlan = (entry) => {
    const pillarsRated = WHEEL_PILLARS.filter((p) => (entry.pillar_scores?.[p.id] || 0) > 0);
    const itemBlocks = allCategories
      .filter((cat) => cat.id !== 'nine-pillars')
      .map((cat) => {
        const allItems = cat.groups ? cat.groups.flatMap((g) => g.items) : (cat.items || []);
        const ticked = allItems.filter((it) => entry.checklist?.[it.id]);
        return ticked.length ? { cat, ticked } : null;
      })
      .filter(Boolean);
    const empty = !pillarsRated.length && !itemBlocks.length;

    return (
      <div className="mt-5 bg-white border border-[#e2d4bf] rounded-2xl p-5" data-testid={`session-plan-${entry.id}`}>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={15} className="text-[#8a6a5a]" />
          <p className="font-heading text-base text-forest">
            {practitioner ? "This session's plan" : `Your plan${planFirstName ? `, ${planFirstName}` : ''}`}
          </p>
        </div>
        <p className="font-body text-xs text-stone-500 mb-4">
          {practitioner
            ? 'Only the exercises you tick appear here — this is exactly what your client sees.'
            : "Here's everything we moved through together this session — take your time with each one, and tap any title whenever you'd like a gentle how-to reminder. 💛"}
        </p>

        {empty ? (
          <p className="font-body text-sm text-stone-400 italic" data-testid={`session-plan-empty-${entry.id}`}>
            {practitioner ? 'Tick exercises above to build the plan.' : 'Nothing has been added to this session yet.'}
          </p>
        ) : (
          <div className="space-y-5">
            {itemBlocks.map(({ cat, ticked }) => (
              <div key={cat.id} data-testid={`session-plan-cat-${entry.id}-${cat.id}`}>
                <p className="font-heading text-sm text-forest mb-2">{cat.name}</p>
                <ul className="space-y-2.5">
                  {ticked.map((it) => {
                    const descOpen = !!openDescs[it.id];
                    const hw = !!entry.homework?.[it.id];
                    const note = entry.item_notes?.[it.id];
                    const videoUrl = it.videoUrl || exerciseVideos[it.id];
                    const label = journalItemLabels[it.id] || it.label;
                    return (
                      <li key={it.id} className="pl-3 border-l-2 border-forest/20" data-testid={`session-plan-item-${entry.id}-${it.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-body text-sm text-forest font-medium leading-snug">
                            {videoUrl ? (
                              <a
                                href={videoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-forest hover:text-terracotta underline decoration-dotted decoration-terracotta/50 underline-offset-4 transition-colors"
                                data-testid={`session-plan-video-${entry.id}-${it.id}`}
                              >
                                <Play size={12} className="text-terracotta flex-shrink-0" /> {label}
                              </a>
                            ) : it.articleUrl ? (
                              <a
                                href={it.articleUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-forest hover:text-terracotta underline decoration-dotted decoration-terracotta/50 underline-offset-4 transition-colors"
                                data-testid={`session-plan-article-${entry.id}-${it.id}`}
                              >
                                <BookOpen size={12} className="text-terracotta flex-shrink-0" /> {label}
                              </a>
                            ) : label}
                            {hw && <span className="ml-1.5 text-[10px] font-body text-[#8a6a5a]">🏠 homework</span>}
                          </span>
                          {it.desc && (
                            <button
                              onClick={() => setOpenDescs((p) => ({ ...p, [it.id]: !p[it.id] }))}
                              className="text-stone-400 hover:text-forest transition-colors flex-shrink-0 mt-0.5"
                              data-testid={`session-plan-desc-toggle-${entry.id}-${it.id}`}
                              aria-label="Show how-to"
                            >
                              <ChevronDown size={15} className={`transition-transform ${descOpen ? 'rotate-180' : ''}`} />
                            </button>
                          )}
                        </div>
                        {it.alt && <span className="block font-body text-[11px] text-stone-400">{it.alt}</span>}
                        {note && <p className="font-body text-xs text-stone-500 mt-0.5 italic">{note}</p>}
                        <AnimatePresence>
                          {it.desc && descOpen && (
                            <motion.p
                              initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                              className="mt-1.5 font-body text-xs text-stone-600 leading-relaxed whitespace-pre-line bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 overflow-hidden"
                              data-testid={`session-plan-desc-${entry.id}-${it.id}`}
                            >
                              {it.desc}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            {pillarsRated.length > 0 && (
              <div data-testid={`session-plan-pillars-${entry.id}`}>
                <p className="font-heading text-sm text-forest mb-2">The 9 Pillars — your wheel</p>
                <ul className="space-y-1 mb-3">
                  {pillarsRated.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2">
                      <span className="font-body text-sm text-stone-600">{p.name}</span>
                      <span className="font-heading text-sm text-forest">{entry.pillar_scores[p.id]} / 10</span>
                    </li>
                  ))}
                </ul>
                {pillarsRated.length >= 3 && (
                  <div className="bg-stone-50 border border-stone-200 rounded-xl p-3">
                    <ResponsiveContainer width="100%" height={240}>
                      <RadarChart data={WHEEL_PILLARS.map((p) => ({ pillar: p.short, score: entry.pillar_scores?.[p.id] || 0 }))} cx="50%" cy="50%" outerRadius="70%">
                        <PolarGrid stroke="#e7e5e4" />
                        <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 10, fill: '#57534e' }} />
                        <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
                        <Radar dataKey="score" stroke="#2f5233" fill="#8BADA4" fillOpacity={0.5} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {(practitioner || (entry.attachments || []).length > 0) && (
          <div className="mt-5 pt-5 border-t border-stone-100">
            {renderAttachments(entry)}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-stone-500 font-body">Loading…</div>;
  }

  if (!bypassGate && (!user || !hasAccess('vip'))) {
    return (
      <div className="min-h-screen bg-cream py-16 px-4" data-testid="journal-access-gate">
        <div className="max-w-2xl mx-auto text-center">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-10">
            <ArrowLeft size={16} /> Back to Programs
          </Link>
          <div className="bg-[#f5efe4] border border-stone-200 rounded-2xl p-10 md:p-14">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-forest/10 text-forest mb-6">
              <Crown size={28} />
            </div>
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em] mb-3">Private Members Area</p>
            <h1 className="font-heading text-3xl md:text-4xl text-forest mb-4">Your Wellness Journal</h1>
            <p className="text-stone-600 font-body leading-relaxed mb-4">
              Your dated session log lives inside the V.I.P. members area — a private space to track what we work on together,
              add notes, and plan your next visit.
            </p>
            <p className="text-stone-600 font-body leading-relaxed mb-8">
              Currently open to <span className="text-forest font-medium">V.I.P. members and package holders</span>. A
              self-serve subscription is <span className="text-forest font-medium">coming soon</span> — reach out and Jill will
              get you set up in the meantime.
            </p>
            <a
              href="mailto:heyjbennett@yahoo.ca?subject=Wellness Journal Access Inquiry"
              className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-6 py-3 rounded-full hover:bg-forest-dark transition-colors"
              data-testid="journal-inquiry-link"
            >
              Ask about access <ChevronRight size={16} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-12 px-4" data-testid="wellness-journal-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-6">
          <ArrowLeft size={16} /> Back to Programs
        </Link>

        <VipSwitcher active={activeTab} />

        {headerSlot}

        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <NotebookPen size={16} className="text-[#8a6a5a]" />
              <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">{eyebrow}</p>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl text-forest">{heading}</h1>
            <p className="font-body text-stone-600 text-sm mt-2 max-w-xl">
              {mirror
                ? "Each session Jill builds with you appears here — your selected exercises, your wellness wheel, and space for your own notes."
                : intro}
            </p>
          </div>
          {showMenu && (
            <button
              onClick={addEntry}
              className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-5 py-3 rounded-full hover:bg-forest-dark transition-colors flex-shrink-0"
              data-testid="journal-add-entry"
            >
              <Plus size={18} /> New session
            </button>
          )}
        </div>

        {mirror && <MyQuizResults userId={owner?.id} />}

        {!fetching && entries.length > 0 && (
          <div className="mb-4 bg-white border border-stone-200 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="journal-history-summary">
            <span className="inline-flex items-center gap-1.5 font-body text-sm text-forest">
              <Calendar size={15} className="text-[#8a6a5a]" />
              <strong className="font-heading">{entries.length}</strong> {entries.length === 1 ? 'session' : 'sessions'}
            </span>
            <span className="font-body text-xs text-stone-500">Last: {prettyDate(entries[0].date)}</span>
            {entries.find((e) => e.next_plan)?.next_plan && (
              <span className="font-body text-xs text-stone-500 basis-full sm:basis-auto sm:ml-auto sm:text-right">
                <span className="text-[#8a6a5a]">Pick up from last time:</span> {entries.find((e) => e.next_plan).next_plan.slice(0, 90)}{entries.find((e) => e.next_plan).next_plan.length > 90 ? '…' : ''}
              </span>
            )}
          </div>
        )}

        {fetching ? (
          <div className="flex items-center gap-2 text-stone-500 font-body py-12 justify-center"><Loader2 size={18} className="animate-spin" /> Loading your journal…</div>
        ) : entries.length === 0 ? (
          <div className="text-center bg-white border border-dashed border-stone-300 rounded-2xl py-14 px-6" data-testid="journal-empty">
            <Calendar size={30} className="text-stone-300 mx-auto mb-3" />
            <p className="font-heading text-lg text-forest mb-1">No sessions yet</p>
            {showMenu ? (
              <>
                <p className="font-body text-stone-500 text-sm mb-5">Tap “New session” to start your first dated entry.</p>
                <button onClick={addEntry} className="inline-flex items-center gap-2 bg-forest text-cream font-body px-5 py-2.5 rounded-full text-sm"><Plus size={16} /> New session</button>
              </>
            ) : (
              <p className="font-body text-stone-500 text-sm">Your sessions with Jill will appear here once you’ve worked together.</p>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="journal-entries">
            {entries.map((entry) => {
              const open = openId === entry.id;
              return (
                <div key={entry.id} className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid={`journal-entry-${entry.id}`}>
                  <button
                    onClick={() => setOpenId(open ? null : entry.id)}
                    className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-stone-50 transition-colors"
                    data-testid={`journal-toggle-${entry.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-forest/8 text-forest flex items-center justify-center flex-shrink-0">
                        <Calendar size={18} />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg text-forest leading-tight">{prettyDate(entry.date)}</h3>
                        <p className="font-body text-xs text-stone-500">{countTicks(entry)} items ticked{entry.session_notes ? ' · has notes' : ''}</p>
                      </div>
                    </div>
                    <ChevronDown size={20} className={`text-forest flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {open && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}>
                        <div className="px-5 pb-6 border-t border-stone-100 pt-4">
                          {/* Date + status */}
                          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                            <label className="flex items-center gap-2 font-body text-sm text-stone-600">
                              <Calendar size={15} className="text-stone-400" /> Session date:
                              {showMenu ? (
                                <input
                                  type="date"
                                  value={entry.date || ''}
                                  onChange={(e) => updateEntry(entry.id, { date: e.target.value })}
                                  className="border border-stone-200 rounded-lg px-2 py-1 text-sm font-body focus:outline-none focus:ring-1 focus:ring-terracotta"
                                  data-testid={`journal-date-${entry.id}`}
                                />
                              ) : (
                                <span className="font-medium text-forest" data-testid={`journal-date-${entry.id}`}>{prettyDate(entry.date)}</span>
                              )}
                            </label>
                            {showMenu && (
                              <span className="text-xs font-body text-stone-400 flex items-center gap-1">
                                {savingId === entry.id ? (<><Loader2 size={12} className="animate-spin" /> Saving…</>) : 'Saved ✓'}
                              </span>
                            )}
                          </div>

                          {/* Checklist categories — collapsible (practitioner + personal journal only) */}
                          {showMenu && (
                          <div className="space-y-3">
                            <ChecklistSearch entry={entry} categories={allCategories} onToggle={toggleItem} onHomework={toggleHomework} showHomework={showHomework} />
                            {allCategories.map((cat) => {
                              const catOpen = !!openCats[cat.id];
                              const isPillars = cat.id === 'nine-pillars';
                              const isZones = Array.isArray(cat.groups);
                              const isGrid = cat.layout === 'grid';
                              let catCount = 0;
                              if (isPillars) catCount = WHEEL_PILLARS.filter((p) => (entry.pillar_scores?.[p.id] || 0) > 0).length;
                              else if (isZones) catCount = cat.groups.reduce((n, g) => n + g.items.filter((it) => entry.checklist?.[it.id]).length, 0);
                              else catCount = (cat.items || []).filter((it) => entry.checklist?.[it.id]).length;
                              const isEmpty = !isPillars && !isZones && (cat.items || []).length === 0;
                              return (
                                <div key={cat.id} className="rounded-xl border border-stone-200 bg-stone-50/50 overflow-hidden">
                                  <button
                                    type="button"
                                    onClick={() => setOpenCats((p) => ({ ...p, [cat.id]: !p[cat.id] }))}
                                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-stone-100/60 transition-colors"
                                    data-testid={`journal-cat-toggle-${entry.id}-${cat.id}`}
                                  >
                                    <div>
                                      <p className="font-heading text-sm text-forest">{cat.name}</p>
                                      {cat.blurb && <p className="font-body text-[11px] text-stone-400">{cat.blurb}</p>}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {catCount > 0 && (
                                        <span className="text-[10px] font-body bg-forest/10 text-forest rounded-full px-2 py-0.5" data-testid={`journal-cat-count-${entry.id}-${cat.id}`}>{catCount}</span>
                                      )}
                                      <ChevronDown size={18} className={`text-forest transition-transform ${catOpen ? 'rotate-180' : ''}`} />
                                    </div>
                                  </button>
                                  <AnimatePresence>
                                    {catOpen && (
                                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                                        {isPillars ? (
                                          /* ── 9 Pillars wellness wheel (1–10 ratings + live radar) ── */
                                          <div className="px-4 pb-5 pt-1">
                                            <div className="space-y-3">
                                              {WHEEL_PILLARS.map((p) => {
                                                const score = entry.pillar_scores?.[p.id] || 0;
                                                return (
                                                  <div key={p.id} data-testid={`journal-pillar-${entry.id}-${p.id}`}>
                                                    <div className="flex items-center justify-between gap-2 mb-1">
                                                      <span className={`font-body text-sm ${score ? 'text-forest font-medium' : 'text-stone-600'}`}>{p.name}</span>
                                                      <span className="font-heading text-sm text-forest w-6 text-right">{score || '–'}</span>
                                                    </div>
                                                    <div className="flex gap-1">
                                                      {[1,2,3,4,5,6,7,8,9,10].map((v) => (
                                                        <button
                                                          key={v}
                                                          onClick={() => setPillarScore(entry, p.id, v)}
                                                          className={`flex-1 h-7 rounded-md text-[11px] font-body transition-colors ${
                                                            score === v
                                                              ? 'bg-forest text-cream'
                                                              : v <= score
                                                                ? 'bg-forest/25 text-forest'
                                                                : 'bg-white border border-stone-200 text-stone-400 hover:border-forest/40'
                                                          }`}
                                                          data-testid={`journal-pillar-score-${entry.id}-${p.id}-${v}`}
                                                        >
                                                          {v}
                                                        </button>
                                                      ))}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                            {catCount >= 3 && (
                                              <div className="mt-5 bg-white border border-stone-200 rounded-xl p-3" data-testid={`journal-wheel-${entry.id}`}>
                                                <p className="text-[10px] font-body uppercase tracking-wider text-stone-500 mb-1 text-center">Your wheel this session</p>
                                                <ResponsiveContainer width="100%" height={260}>
                                                  <RadarChart data={WHEEL_PILLARS.map((p) => ({ pillar: p.short, score: entry.pillar_scores?.[p.id] || 0 }))} cx="50%" cy="50%" outerRadius="70%">
                                                    <PolarGrid stroke="#e7e5e4" />
                                                    <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 10, fill: '#57534e' }} />
                                                    <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
                                                    <Radar dataKey="score" stroke="#2f5233" fill="#8BADA4" fillOpacity={0.5} />
                                                  </RadarChart>
                                                </ResponsiveContainer>
                                              </div>
                                            )}
                                            {catCount > 0 && catCount < 3 && (
                                              <p className="mt-3 text-center font-body text-[11px] text-stone-400">Rate at least 3 pillars to see the wheel.</p>
                                            )}
                                          </div>
                                        ) : isZones ? (
                                          /* ── Grouped section (zones or named sub-groups) ── */
                                          <div className="px-4 pb-4 pt-1 space-y-4">
                                            {cat.note && (
                                              <p className="font-body text-[11px] text-forest/80 bg-forest/5 border border-forest/10 rounded-lg px-3 py-2 leading-relaxed" data-testid={`journal-note-${entry.id}-${cat.id}`}>{cat.note}</p>
                                            )}
                                            {cat.groups.map((g) => (
                                              <div key={g.zone || g.title || 'main'} data-testid={`journal-group-${entry.id}-${g.zone || g.title || 'main'}`}>
                                                {(g.zone || g.title) && (
                                                  <div className="flex items-baseline gap-2 mb-2">
                                                    {g.zone ? (
                                                      <>
                                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-forest/10 text-forest font-heading text-xs flex-shrink-0">{g.zone}</span>
                                                        {g.desc && <span className="font-body text-[11px] text-stone-400 leading-tight">{g.desc}</span>}
                                                      </>
                                                    ) : (
                                                      <span className="font-heading text-xs text-forest uppercase tracking-wider">{g.title}</span>
                                                    )}
                                                  </div>
                                                )}
                                                <ul className={cat.descriptions ? 'space-y-1.5' : 'grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2'}>
                                                  {g.items.map((it) => {
                                                    const checked = !!entry.checklist?.[it.id];
                                                    const descOpen = !!openDescs[it.id];
                                                    const hw = !!entry.homework?.[it.id];
                                                    return (
                                                      <li key={it.id} className="flex items-start gap-2">
                                                        <button
                                                          onClick={() => toggleItem(entry, it.id)}
                                                          className={`mt-0.5 flex-shrink-0 transition-colors ${checked ? 'text-forest' : 'text-stone-300 hover:text-forest'}`}
                                                          data-testid={`journal-check-${entry.id}-${it.id}`}
                                                        >
                                                          {checked ? <Check size={16} /> : <Square size={16} />}
                                                        </button>
                                                        <div className="flex-1 min-w-0">
                                                          <div className="flex items-start justify-between gap-2">
                                                            <span className={`font-body text-sm ${checked ? 'text-forest font-medium' : 'text-stone-600'}`}>
                                                              {it.label}
                                                              {hw && <span className="ml-1 text-[10px] font-body text-[#8a6a5a]">🏠</span>}
                                                            </span>
                                                            <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                                                              {showHomework && (
                                                                <button
                                                                  onClick={() => toggleHomework(entry, it.id)}
                                                                  className={`transition-colors ${hw ? 'text-[#8a6a5a]' : 'text-stone-300 hover:text-[#8a6a5a]'}`}
                                                                  data-testid={`journal-hw-${entry.id}-${it.id}`}
                                                                  title="Assign as homework"
                                                                >
                                                                  <Home size={14} />
                                                                </button>
                                                              )}
                                                              {it.desc && (
                                                                <button
                                                                  onClick={() => setOpenDescs((p) => ({ ...p, [it.id]: !p[it.id] }))}
                                                                  className="text-stone-400 hover:text-forest transition-colors"
                                                                  data-testid={`journal-desc-toggle-${entry.id}-${it.id}`}
                                                                  aria-label="Show how-to"
                                                                >
                                                                  <ChevronDown size={15} className={`transition-transform ${descOpen ? 'rotate-180' : ''}`} />
                                                                </button>
                                                              )}
                                                            </div>
                                                          </div>
                                                          {it.alt && <span className="block font-body text-[11px] text-stone-400">{it.alt}</span>}
                                                          <AnimatePresence>
                                                            {it.desc && descOpen && (
                                                              <motion.p
                                                                initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                                                                className="mt-1 font-body text-xs text-stone-600 leading-relaxed whitespace-pre-line bg-white border border-stone-200 rounded-lg px-3 py-2 overflow-hidden"
                                                                data-testid={`journal-desc-${entry.id}-${it.id}`}
                                                              >
                                                                {it.desc}
                                                              </motion.p>
                                                            )}
                                                          </AnimatePresence>
                                                          {checked && (
                                                            <input
                                                              type="text"
                                                              value={entry.item_notes?.[it.id] || ''}
                                                              onChange={(e) => setItemNote(entry, it.id, e.target.value)}
                                                              placeholder="＋ note…"
                                                              className="mt-1 w-full text-xs font-body text-stone-700 bg-white border border-stone-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-terracotta placeholder:text-stone-400"
                                                              data-testid={`journal-itemnote-${entry.id}-${it.id}`}
                                                            />
                                                          )}
                                                        </div>
                                                      </li>
                                                    );
                                                  })}
                                                </ul>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          /* ── Default checklist (list or grid) ── */
                                          (isEmpty && !cat.custom) ? (
                                            <p className="px-4 pb-4 pt-1 font-body text-[11px] text-stone-400 italic">Nothing added here yet.</p>
                                          ) : (
                                          <div className="px-4 pb-4 pt-1 space-y-3">
                                          {cat.items.length > 0 && (
                                          <ul className={isGrid ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2' : 'space-y-2'}>
                                            {cat.items.map((it) => {
                                              const checked = !!entry.checklist?.[it.id];
                                              const hw = !!entry.homework?.[it.id];
                                              const descOpen = !!openDescs[it.id];
                                              return (
                                                <li key={it.id} className="flex items-start gap-2">
                                                  <button
                                                    onClick={() => toggleItem(entry, it.id)}
                                                    className={`mt-0.5 flex-shrink-0 transition-colors ${checked ? 'text-forest' : 'text-stone-300 hover:text-forest'}`}
                                                    data-testid={`journal-check-${entry.id}-${it.id}`}
                                                  >
                                                    {checked ? <Check size={16} /> : <Square size={16} />}
                                                  </button>
                                                  <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-2">
                                                      <span className={`font-body text-sm ${checked ? 'text-forest font-medium' : 'text-stone-600'}`}>
                                                        {it.label}
                                                        {hw && <span className="ml-1 text-[10px] font-body text-[#8a6a5a]">🏠</span>}
                                                      </span>
                                                      <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                                                        {showHomework && (
                                                          <button
                                                            onClick={() => toggleHomework(entry, it.id)}
                                                            className={`transition-colors ${hw ? 'text-[#8a6a5a]' : 'text-stone-300 hover:text-[#8a6a5a]'}`}
                                                            data-testid={`journal-hw-${entry.id}-${it.id}`}
                                                            title="Assign as homework"
                                                          >
                                                            <Home size={14} />
                                                          </button>
                                                        )}
                                                        {it.desc && (
                                                          <button
                                                            onClick={() => setOpenDescs((p) => ({ ...p, [it.id]: !p[it.id] }))}
                                                            className="text-stone-400 hover:text-forest transition-colors"
                                                            data-testid={`journal-desc-toggle-${entry.id}-${it.id}`}
                                                            aria-label="Show how-to"
                                                          >
                                                            <ChevronDown size={15} className={`transition-transform ${descOpen ? 'rotate-180' : ''}`} />
                                                          </button>
                                                        )}
                                                        {cat.custom && practitioner && (
                                                          <button
                                                            onClick={() => { if (window.confirm(`Delete "${it.label}" from My Exercises?`)) deleteCustomExercise(it.id); }}
                                                            className="text-stone-300 hover:text-red-500 transition-colors"
                                                            data-testid={`journal-delete-exercise-${it.id}`}
                                                            title="Delete this exercise"
                                                          >
                                                            <Trash2 size={14} />
                                                          </button>
                                                        )}
                                                      </div>
                                                    </div>
                                                    <AnimatePresence>
                                                      {it.desc && descOpen && (
                                                        <motion.p
                                                          initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                                                          className="mt-1 font-body text-xs text-stone-600 leading-relaxed whitespace-pre-line bg-white border border-stone-200 rounded-lg px-3 py-2 overflow-hidden"
                                                        >
                                                          {it.desc}
                                                        </motion.p>
                                                      )}
                                                    </AnimatePresence>
                                                    {checked && (
                                                      <input
                                                        type="text"
                                                        value={entry.item_notes?.[it.id] || ''}
                                                        onChange={(e) => setItemNote(entry, it.id, e.target.value)}
                                                        placeholder="＋ note…"
                                                        className="mt-1 w-full text-xs font-body text-stone-700 bg-white border border-stone-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-terracotta placeholder:text-stone-400"
                                                        data-testid={`journal-itemnote-${entry.id}-${it.id}`}
                                                      />
                                                    )}
                                                  </div>
                                                </li>
                                              );
                                            })}
                                          </ul>
                                          )}
                                          {cat.custom && practitioner && (
                                            <AddCustomExercise onAdd={addCustomExercise} />
                                          )}
                                          </div>
                                          )
                                        )}
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              );
                            })}
                          </div>
                          )}

                          {/* Session plan — only the ticked exercises (what the client sees) */}
                          {showPlan && renderPlan(entry)}

                          {/* Session notes + next plan */}
                          <div className="mt-5 space-y-4">
                            <div>
                              <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">What we did today</label>
                              {showMenu ? (
                                <textarea
                                  rows={3}
                                  value={entry.session_notes || ''}
                                  onChange={(e) => updateEntry(entry.id, { session_notes: e.target.value })}
                                  placeholder="Notes from this session…"
                                  className="w-full font-body text-sm text-stone-700 bg-white border border-stone-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-terracotta"
                                  data-testid={`journal-session-notes-${entry.id}`}
                                />
                              ) : entry.session_notes ? (
                                <p className="font-body text-sm text-stone-700 whitespace-pre-wrap bg-white border border-stone-200 rounded-xl px-3 py-2" data-testid={`journal-session-notes-${entry.id}`}>{entry.session_notes}</p>
                              ) : (
                                <p className="font-body text-sm text-stone-400 italic">No notes for this session.</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Plan for next session</label>
                              {showMenu ? (
                                <textarea
                                  rows={2}
                                  value={entry.next_plan || ''}
                                  onChange={(e) => updateEntry(entry.id, { next_plan: e.target.value })}
                                  placeholder="What to focus on next time…"
                                  className="w-full font-body text-sm text-stone-700 bg-white border border-stone-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-terracotta"
                                  data-testid={`journal-next-plan-${entry.id}`}
                                />
                              ) : entry.next_plan ? (
                                <p className="font-body text-sm text-stone-700 whitespace-pre-wrap bg-white border border-stone-200 rounded-xl px-3 py-2" data-testid={`journal-next-plan-${entry.id}`}>{entry.next_plan}</p>
                              ) : (
                                <p className="font-body text-sm text-stone-400 italic">Nothing planned yet.</p>
                              )}
                            </div>

                            {showHomework && (
                              <div className="bg-[#f3ece0] border border-[#e2d4bf] rounded-xl p-3" data-testid={`journal-homework-${entry.id}`}>
                                <div className="flex items-center gap-1.5 mb-2">
                                  <Home size={13} className="text-[#8a6a5a]" />
                                  <p className="text-[10px] font-body uppercase tracking-wider text-[#8a6a5a]">Homework</p>
                                </div>
                                {(() => {
                                  const hwList = categories
                                    .flatMap((c) => (c.groups ? c.groups.flatMap((g) => g.items) : (c.items || [])))
                                    .filter((it) => entry.homework?.[it.id]);
                                  const doneCount = hwList.filter((it) => entry.homework_done?.[it.id]).length;
                                  return hwList.length ? (
                                    <>
                                      <p className="text-[10px] font-body text-stone-500 mb-1.5">{doneCount}/{hwList.length} done · tap a box to check off</p>
                                      <ul className="mb-2 space-y-1">
                                        {hwList.map((it) => {
                                          const done = !!entry.homework_done?.[it.id];
                                          return (
                                            <li key={it.id} className="flex items-center gap-2">
                                              <button
                                                onClick={() => toggleHomeworkDone(entry, it.id)}
                                                className={`flex-shrink-0 transition-colors ${done ? 'text-[#8a6a5a]' : 'text-stone-300 hover:text-[#8a6a5a]'}`}
                                                data-testid={`journal-hw-done-${entry.id}-${it.id}`}
                                                title={done ? 'Mark as not done' : 'Mark as done'}
                                              >
                                                {done ? <Check size={16} /> : <Square size={16} />}
                                              </button>
                                              <span className={`font-body text-sm ${done ? 'text-stone-400 line-through' : 'text-forest'}`}>
                                                {journalItemLabels[it.id] || it.label}
                                              </span>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </>
                                  ) : (
                                    <p className="font-body text-[11px] text-stone-400 mb-2 italic">
                                      {practitioner
                                        ? 'Tap the 🏠 beside any exercise above to assign it as homework.'
                                        : 'No homework assigned this session.'}
                                    </p>
                                  );
                                })()}
                                <textarea
                                  rows={2}
                                  value={entry.homework_note || ''}
                                  onChange={(e) => updateEntry(entry.id, { homework_note: e.target.value })}
                                  placeholder="Homework notes for this week…"
                                  className="w-full font-body text-sm text-stone-700 bg-white border border-stone-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-terracotta"
                                  data-testid={`journal-homework-note-${entry.id}`}
                                />
                              </div>
                            )}

                            {entry.practitioner_notes && (
                              <div className="bg-[#f5efe4] border border-[#e8dcc3] rounded-xl p-3">
                                <p className="text-[10px] font-body uppercase tracking-wider text-[#8a6a5a] mb-1">A note from Jill</p>
                                <p className="font-body text-sm text-stone-700 whitespace-pre-wrap">{entry.practitioner_notes}</p>
                              </div>
                            )}

                            {mirror && (
                              <div data-testid={`journal-my-notes-wrap-${entry.id}`}>
                                <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1 flex items-center gap-1.5">
                                  <NotebookPen size={12} className="text-[#8a6a5a]" /> My notes
                                </label>
                                <textarea
                                  rows={3}
                                  value={entry.client_notes || ''}
                                  onChange={(e) => updateEntry(entry.id, { client_notes: e.target.value })}
                                  placeholder="Jot down how this felt, questions for Jill, wins to remember…"
                                  className="w-full font-body text-sm text-stone-700 bg-white border border-stone-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-terracotta"
                                  data-testid={`journal-my-notes-${entry.id}`}
                                />
                              </div>
                            )}
                          </div>

                          <div className="mt-5 flex items-center justify-between gap-3">
                            <button
                              onClick={() => downloadSessionPdf(entry)}
                              className="inline-flex items-center gap-1.5 bg-[#8a6a5a] text-cream text-xs font-body font-medium px-4 py-2 rounded-full hover:bg-[#755848] transition-colors"
                              data-testid={`journal-pdf-${entry.id}`}
                            >
                              <FileDown size={14} /> Download PDF
                            </button>
                            {showMenu && (
                              <button
                                onClick={() => deleteEntry(entry.id)}
                                className="inline-flex items-center gap-1.5 text-stone-400 hover:text-red-600 text-xs font-body transition-colors"
                                data-testid={`journal-delete-${entry.id}`}
                              >
                                <Trash2 size={13} /> Delete session
                              </button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WellnessJournalPage;
